/**
 * Agent A6c — Board Narrator (V85-4-4 / gap E4, R-85-05).
 *
 * Generates board-ready narrative from blueprint inventory and gap analysis.
 * The narrative preserves epistemic honesty:
 *   - Unknown slots are "not yet assessed", never "confirmed gaps"
 *   - Detected configuration is "observed", never "proven effective"
 *   - Inferred evidence is clearly labelled as inferred
 *   - Conflicting signals are surfaced, not silently resolved
 *
 * R-85-05 rules enforced:
 *   - Unknown != confirmed gap
 *   - Detected configuration != proven effectiveness
 *   - No invented coverage in narrative
 *   - Uncertainty is preserved, not collapsed
 *
 * Runs through the V85-4-0 agent harness. Output writes to
 * `proposed_changes` queue (narrative is a proposal for editor review).
 */
import { defineTemplate, derive } from '../egress/data-classes.js';
import { LlmRefusedError } from '../egress/gateway.js';
// ---- Agent definition -------------------------------------------------------
export const A6C_DEFINITION = {
    agent_id: 'A6c',
    prompt_version: '2026.09.20',
    queue_target: 'proposed_changes',
    schema_version: '1.0',
};
export const A6C_OUTPUT_SCHEMA = {
    type: 'object',
    required: ['title', 'executive_summary', 'sections', 'unassessed_slots', 'confidence_ratio'],
    properties: {
        title: { type: 'string' },
        executive_summary: { type: 'string' },
        sections: { type: 'array' },
        unassessed_slots: { type: 'array' },
        confidence_ratio: { type: 'number' },
    },
};
// ---- Epistemic classification -----------------------------------------------
const STRENGTH_TO_EPISTEMIC = {
    directly_observed: 'confirmed',
    inferred: 'inferred',
    user_supplied: 'observed',
};
/**
 * Classify a slot's epistemic level from its observations. Pure function.
 *
 * Rules:
 *   - No observations → 'unknown'
 *   - Any directly_observed → 'confirmed' (but only for configuration
 *     presence, NOT effectiveness — the narrative must say "observed" not
 *     "proven effective")
 *   - Only inferred → 'inferred'
 *   - Only user_supplied → 'observed'
 *   - Mixed: highest strength wins, but the narrative must note the mix
 */
export function classifyEpistemicLevel(observations) {
    if (observations.length === 0)
        return 'unknown';
    const strengths = observations.map((o) => o.claim_strength);
    if (strengths.includes('directly_observed'))
        return 'confirmed';
    if (strengths.includes('user_supplied'))
        return 'observed';
    return 'inferred';
}
// ---- Deterministic narrative generation -------------------------------------
/**
 * Generate the narrative deterministically from inventory + gaps + roadmap.
 * The LLM can later polish the prose, but the structure and epistemic
 * qualifiers are computed here and are not negotiable.
 */
export function generateDeterministicNarrative(input) {
    const slotIndex = new Map();
    for (const s of input.ra_slots)
        slotIndex.set(s.slot_id, s);
    // Group observations by slot
    const obsBySlot = new Map();
    for (const obs of input.observations) {
        const list = obsBySlot.get(obs.slot_id) ?? [];
        list.push(obs);
        obsBySlot.set(obs.slot_id, list);
    }
    const gapSet = new Set(input.gaps.map((g) => g.slot_id));
    const allSlotIds = input.ra_slots.map((s) => s.slot_id);
    // Identify unassessed slots (no observations at all)
    const unassessedSlots = allSlotIds.filter((id) => !obsBySlot.has(id) || obsBySlot.get(id).length === 0);
    // Build sections
    const sections = [];
    // Section 1: Coverage overview
    const observedCount = allSlotIds.length - unassessedSlots.length;
    const gapCount = input.gaps.length;
    const criticalGaps = input.gaps.filter((g) => g.severity === 'critical').length;
    const highGaps = input.gaps.filter((g) => g.severity === 'high').length;
    sections.push({
        heading: 'Coverage Overview',
        body: `Of ${allSlotIds.length} reference architecture capabilities, ` +
            `${observedCount} have been assessed with evidence. ` +
            `${unassessedSlots.length} remain unassessed and require further investigation. ` +
            `${gapCount} gap(s) identified (${criticalGaps} critical, ${highGaps} high).`,
        epistemic_level: 'confirmed',
        source_refs: ['blueprint-inventory-pass-1'],
    });
    // Section 2: Strong capabilities (confirmed/observed with native fit)
    const strongSlots = allSlotIds.filter((id) => {
        const obs = obsBySlot.get(id) ?? [];
        if (obs.length === 0)
            return false;
        const level = classifyEpistemicLevel(obs);
        return (level === 'confirmed' || level === 'observed') && !gapSet.has(id);
    });
    if (strongSlots.length > 0) {
        const strongItems = strongSlots.map((id) => {
            const slot = slotIndex.get(id);
            const obs = obsBySlot.get(id) ?? [];
            // Configuration detected != proven effective
            return `${slot?.role ?? id}: ${obs[0]?.claim ?? 'configuration detected'} (observed, not yet validated for effectiveness)`;
        });
        sections.push({
            heading: 'Capabilities with Evidence',
            body: strongItems.join('. ') + '.',
            epistemic_level: 'observed',
            source_refs: strongSlots.map((id) => `observations:${id}`),
        });
    }
    // Section 3: Gaps requiring attention
    if (gapCount > 0) {
        const gapItems = input.gaps.map((g) => {
            const slot = slotIndex.get(g.slot_id);
            const obs = obsBySlot.get(g.slot_id) ?? [];
            const hasPartialEvidence = obs.length > 0;
            if (hasPartialEvidence) {
                return `${slot?.role ?? g.slot_id}: partial evidence observed ` +
                    `(${obs[0]?.claim ?? 'see details'}) — ` +
                    `gap severity: ${g.severity}, effort: ${g.effort_band}`;
            }
            return `${slot?.role ?? g.slot_id}: no evidence observed — ` +
                `gap severity: ${g.severity}, effort: ${g.effort_band}`;
        });
        sections.push({
            heading: 'Gaps Requiring Attention',
            body: gapItems.join('. ') + '.',
            epistemic_level: gapCount > 0 && input.gaps.some((g) => (obsBySlot.get(g.slot_id) ?? []).length > 0) ? 'observed' : 'unknown',
            source_refs: input.gaps.map((g) => `gap:${g.slot_id}`),
        });
    }
    // Section 4: Unassessed capabilities
    if (unassessedSlots.length > 0) {
        const unassessedItems = unassessedSlots.map((id) => {
            const slot = slotIndex.get(id);
            return `${slot?.role ?? id}: not yet assessed — this is not a confirmed gap, ` +
                `it means we have no evidence either way`;
        });
        sections.push({
            heading: 'Not Yet Assessed',
            body: unassessedItems.join('. ') + '.',
            epistemic_level: 'unknown',
            source_refs: unassessedSlots.map((id) => `unassessed:${id}`),
        });
    }
    // Section 5: Roadmap summary
    if (input.roadmap.length > 0) {
        const deadlineItems = input.roadmap
            .filter((r) => r.regulatory_deadline)
            .slice(0, 5);
        const roadmapBody = deadlineItems.length > 0
            ? `${input.roadmap.length} roadmap item(s). ` +
                `${deadlineItems.length} have regulatory deadlines: ` +
                deadlineItems
                    .map((r) => `${r.gap_slot_id} (by ${r.regulatory_deadline})`)
                    .join(', ') + '.'
            : `${input.roadmap.length} roadmap item(s), none with regulatory deadlines.`;
        sections.push({
            heading: 'Remediation Roadmap',
            body: roadmapBody,
            epistemic_level: 'confirmed',
            source_refs: ['blueprint-roadmap-pass-4'],
        });
    }
    // Confidence ratio
    const confirmedOrObserved = allSlotIds.filter((id) => {
        const obs = obsBySlot.get(id) ?? [];
        const level = classifyEpistemicLevel(obs);
        return level === 'confirmed' || level === 'observed';
    }).length;
    const confidenceRatio = allSlotIds.length > 0
        ? confirmedOrObserved / allSlotIds.length
        : 0;
    // Executive summary
    const orgPrefix = input.org_name ? `${input.org_name}: ` : '';
    const executiveSummary = `${orgPrefix}As of ${input.as_of}, ${observedCount} of ${allSlotIds.length} ` +
        `AI governance capabilities have evidence. ` +
        `${gapCount} gap(s) require attention (${criticalGaps} critical). ` +
        `${unassessedSlots.length} capabilities have not yet been assessed — ` +
        `these are unknowns, not confirmed gaps. ` +
        `Overall evidence confidence: ${(confidenceRatio * 100).toFixed(0)}%.`;
    return {
        title: `AI Governance Blueprint Assessment — ${input.as_of}`,
        executive_summary: executiveSummary,
        sections,
        unassessed_slots: unassessedSlots,
        confidence_ratio: Math.round(confidenceRatio * 100) / 100,
    };
}
// ---- Agent function ---------------------------------------------------------
export const boardNarratorAgent = async (llm, input) => {
    const data = input.data;
    // 1. Deterministic narrative with epistemic qualifiers
    const narrative = generateDeterministicNarrative(data);
    // 2. Optionally polish prose with LLM (graceful degradation)
    try {
        // ADR-0073: through the egress gateway; a refusal keeps the deterministic narrative.
        const r = await llm.run({
            task: 'agent.a6c.board_narrative', template: A6C_TEMPLATE, slots: promptSlots(narrative),
            schemaHint: A6C_OUTPUT_SCHEMA,
        });
        if (r.status !== 'ok')
            throw new LlmRefusedError(r);
        const llmResult = { data: r.output };
        if (llmResult.data?.executive_summary) {
            // Validate: LLM summary must not remove uncertainty language
            const llmSummary = llmResult.data.executive_summary;
            if (narrative.unassessed_slots.length > 0 &&
                !llmSummary.includes('not yet assessed') &&
                !llmSummary.includes('unknown') &&
                !llmSummary.includes('not confirmed')) {
                // LLM collapsed uncertainty — reject and keep deterministic
            }
            else {
                narrative.executive_summary = llmSummary;
            }
        }
    }
    catch {
        // LLM failure: deterministic narrative is sufficient
    }
    const provenance = {
        title: {
            source: 'deterministic-generation',
            confidence: 1.0,
        },
        executive_summary: {
            source: 'deterministic-generation',
            span: narrative.executive_summary.slice(0, 100),
            confidence: 0.9,
        },
        sections: {
            source: 'blueprint-inventory-and-gap-analysis',
            span: `${narrative.sections.length} sections`,
            confidence: 0.9,
        },
        unassessed_slots: {
            source: 'blueprint-inventory-pass-1',
            span: narrative.unassessed_slots.join(', ') || 'none',
            confidence: 1.0,
        },
        confidence_ratio: {
            source: 'deterministic-computation',
            span: String(narrative.confidence_ratio),
            confidence: 1.0,
        },
    };
    const output = {
        target: 'proposed_changes',
        proposal: {
            title: narrative.title,
            executive_summary: narrative.executive_summary,
            sections: narrative.sections,
            unassessed_slots: narrative.unassessed_slots,
            confidence_ratio: narrative.confidence_ratio,
        },
        provenance,
    };
    return output;
};
/** ADR-0073: the A6c prompt as a reviewed template; the narrative (from the inventory) is tenant data. */
const A6C_PROMPT = [
    'You are writing a board-ready AI governance assessment narrative.',
    'CRITICAL RULES:',
    '- Unknown slots are "not yet assessed", NEVER "confirmed gaps"',
    '- Detected configuration is "observed", NEVER "proven effective"',
    '- Inferred evidence must be labelled as such',
    '- Conflicting signals must be surfaced, not silently resolved',
    '- Do NOT add coverage claims not backed by evidence',
    '',
    '## Current Executive Summary',
    '{executive_summary}',
    '',
    '## Sections',
    '{sections}',
    '',
    '## Unassessed slots: {unassessed}',
    '',
    'Polish the prose for a board audience. Preserve ALL uncertainty qualifiers.',
    'Return JSON: { "executive_summary": "...", "sections": [{ "heading", "body", "slot_id?" }] }',
].join('\n');
export const A6C_TEMPLATE = defineTemplate('agent.a6c.board_narrative', '2026.09.20', A6C_PROMPT, ['executive_summary', 'sections', 'unassessed']);
function promptSlots(narrative) {
    const inv = (v) => derive('blueprint.inventory', v, ['blueprint.inventory']);
    return {
        executive_summary: inv(narrative.executive_summary),
        sections: inv(narrative.sections.map((s) => `### ${s.heading} [${s.epistemic_level}]\n${s.body}`).join('\n')),
        unassessed: inv(narrative.unassessed_slots.join(', ') || 'none'),
    };
}
//# sourceMappingURL=agent-board-narrator-service.js.map